import { useEffect, useRef, useState } from "react";
import styled from "styled-components";

import products from "../data/products";

/* Hardcoded demo basket — no state, no storage, no checkout.
   Change the ids/quantities here to show different products on camera. */
const ITEMS = [
  { id: "linea", qty: 1 },
  { id: "volta", qty: 2 },
];

const lines = ITEMS.map(({ id, qty }) => {
  const p = products.find((x) => x.id === id);
  return p ? { ...p, qty } : null;
}).filter(Boolean);

const count = lines.reduce((n, l) => n + l.qty, 0);
const subtotal = lines.reduce((n, l) => n + l.price * l.qty, 0);

export default function Cart() {
  const [open, setOpen] = useState(false);
  const wrapRef = useRef(null);

  /* close on outside click and on Escape */
  useEffect(() => {
    if (!open) return;

    const onDown = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target))
        setOpen(false);
    };
    const onKey = (e) => e.key === "Escape" && setOpen(false);

    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <Wrap ref={wrapRef}>
      <Trigger
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-label={`Shporta — ${count} artikuj`}
      >
        <Icon viewBox="0 0 24 24" aria-hidden="true">
          <path d="M4 7h16l-1.3 12.1a2 2 0 0 1-2 1.9H7.3a2 2 0 0 1-2-1.9L4 7Z" />
          <path d="M9 7V5.6A3 3 0 0 1 12 2.6a3 3 0 0 1 3 3V7" />
        </Icon>
        {count > 0 && <Badge>{count}</Badge>}
      </Trigger>

      <Panel $open={open} role="dialog" aria-label="Shporta">
        <Head>
          <HeadTitle>Shporta</HeadTitle>
          <HeadCount>{count} artikuj</HeadCount>
        </Head>

        <List>
          {lines.map((l) => (
            <Line key={l.id}>
              <Thumb src={l.off} alt="" />
              <LineInfo>
                <LineName>{l.name}</LineName>
                <LineType>{l.type}</LineType>
                <LineQty>Sasia: {l.qty}</LineQty>
              </LineInfo>
              <LinePrice>€{l.price * l.qty}</LinePrice>
            </Line>
          ))}
        </List>

        <Sum>
          <span>Totali</span>
          <SumValue>€{subtotal}</SumValue>
        </Sum>

        <Go type="button" onClick={() => setOpen(false)}>
          Vazhdo te pagesa
        </Go>
        <Note>Demo — pagesa nuk është aktive.</Note>
      </Panel>
    </Wrap>
  );
}

/* ------------------------------------------------------------------ */

const Wrap = styled.div`
  position: relative;
`;

const Trigger = styled.button`
  position: relative;
  width: 44px;
  height: 44px;
  display: grid;
  place-items: center;
  border-radius: 50%;
  transition: background 260ms var(--ease);

  &:hover {
    background: color-mix(in srgb, var(--ink) 8%, transparent);
  }

  &:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: 2px;
  }
`;

const Icon = styled.svg`
  width: 21px;
  height: 21px;
  fill: none;
  stroke: var(--ink);
  stroke-width: 1.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  transition: stroke var(--t-theme) var(--ease);
`;

const Badge = styled.span`
  position: absolute;
  top: 4px;
  right: 3px;
  min-width: 17px;
  height: 17px;
  padding: 0 4px;
  display: grid;
  place-items: center;
  border-radius: 999px;
  background: var(--accent);
  color: #fff;
  font-size: 10px;
  line-height: 1;
  transition: background var(--t-theme) var(--ease);
`;

const Panel = styled.div`
  position: absolute;
  top: calc(100% + 14px);
  right: 0;
  z-index: 40;
  width: min(340px, calc(100vw - 32px));
  padding: 22px;
  border-radius: 4px;
  border: 1px solid var(--line);
  background: var(--bg);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.22);

  opacity: ${({ $open }) => ($open ? 1 : 0)};
  visibility: ${({ $open }) => ($open ? "visible" : "hidden")};
  transform: translateY(${({ $open }) => ($open ? "0" : "-8px")});
  transition:
    opacity 260ms var(--ease),
    transform 260ms var(--ease),
    visibility 260ms,
    background var(--t-theme) var(--ease),
    border-color var(--t-theme) var(--ease);
`;

const Head = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  padding-bottom: 14px;
  border-bottom: 1px solid var(--line);
`;

const HeadTitle = styled.span`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 21px;
`;

const HeadCount = styled.span`
  font-size: 10px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const List = styled.div`
  display: flex;
  flex-direction: column;
`;

const Line = styled.div`
  display: flex;
  align-items: center;
  gap: 13px;
  padding: 15px 0;
  border-bottom: 1px solid var(--line);
`;

const Thumb = styled.img`
  width: 46px;
  height: 58px;
  object-fit: cover;
  border-radius: 2px;
  background: var(--bg-soft);
  flex-shrink: 0;
`;

const LineInfo = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const LineName = styled.span`
  font-family: "Instrument Serif", Georgia, serif;
  font-size: 17px;
`;

const LineType = styled.span`
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const LineQty = styled.span`
  font-size: 11px;
  color: var(--ink-soft);
  margin-top: 2px;
`;

const LinePrice = styled.span`
  margin-left: auto;
  font-size: 13px;
  white-space: nowrap;
`;

const Sum = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: baseline;
  padding: 16px 0 18px;
  font-size: 11px;
  letter-spacing: 0.18em;
  text-transform: uppercase;
  color: var(--ink-soft);
`;

const SumValue = styled.span`
  font-size: 19px;
  letter-spacing: 0;
  text-transform: none;
  color: var(--ink);
`;

const Go = styled.button`
  width: 100%;
  padding: 15px;
  border-radius: 999px;
  background: var(--ink);
  color: var(--bg);
  font-size: 12px;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  transition:
    background var(--t-theme) var(--ease),
    color var(--t-theme) var(--ease),
    opacity 200ms var(--ease);

  &:hover {
    opacity: 0.86;
  }
`;

const Note = styled.p`
  margin-top: 11px;
  text-align: center;
  font-size: 10px;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--accent);
`;
